import React, { useEffect, useState, useCallback } from "react";
import usePersistentState from "../../../../hooks/usePersistentState";
import {
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Autocomplete,
  Paper,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Badge,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox
} from "@mui/material";
import { countries } from "country-list-json";
import {
  DataGrid,
  GridToolbar,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SortIcon from '@mui/icons-material/Sort';

import ReportList from "../Reports/reports";
import Header from "../../../Header1";
import { tokens } from "../../../../theme";
import * as adminUserAction from "../../../../actions/admin/user";
import { getProfileImageUrl } from "../../../../utils/serverUtils";
import { reasons } from "../../../../utils/constants";
import RejectNewUserModal from "../RejectNewUserModal";

const Contacts = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = usePersistentState("admin_approval_page_v2", 0);
  const [pageSize, setPageSize] = usePersistentState("admin_approval_pageSize_v2", 25);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = usePersistentState("admin_approval_filterModel_v2", { items: [] });
  const [tabValue, setTabValue] = usePersistentState("admin_approval_tab_v2", 0);
  const [selectionModel, setSelectionModel] = useState([]);
  const [sortModel, setSortModel] = usePersistentState("admin_approval_sort_v2", [{ field: 'updated_at', sort: 'desc' }]);
  const [selectedCountry, setSelectedCountry] = usePersistentState("admin_approval_country_v2", null);
  const [selectedGender, setSelectedGender] = usePersistentState("admin_approval_gender_v2", "");
  const [approvalStatus, setApprovalStatus] = usePersistentState("admin_approval_status_v2", "10"); // 10 = Pending, 30 = Rejected
  
  // State for Reject Modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [selectedReason, setSelectedReason] = useState("");

  // Handler for Reject Modal Input
  const handleSelectChange = (event) => {
    const selected = event.target.value;
    setSelectedReason(selected);
    setCustomReason("");
    setRejectReason(selected);
  };

  const handleCustomReasonChange = (event) => {
    const customText = event.target.value;
    setCustomReason(customText);
    setSelectedReason("");
    setRejectReason(customText);
  };

  const handleCloseRejectModal = () => {
      setShowRejectModal(false);
      setRejectReason("");
      setCustomReason("");
      setSelectedReason("");
  };

  const handleSaveReject = async () => {
      try {
          setLoading(true);
          await Promise.all(selectionModel.map(async (id) => {
              await adminUserAction.updateUserStatus({ 
                  id, 
                  approval: 30, // Rejected status
                  message: rejectReason 
              });
              
              // Notify user via socket
              await adminUserAction.updateUserSocket(id, {
                  event: "user_rejected",
              });
          }));

          setSelectionModel([]);
          handleCloseRejectModal();
          await fetchData();
      } catch (err) {
          console.error("Error rejecting users:", err);
      } finally {
          setLoading(false);
      }
  };
  
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:768px)");

  const containsOperator = getGridStringOperators().find(
    (op) => op.value === "contains"
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const isClientFilter = selectedCountry || selectedGender;

      const queryParams = new URLSearchParams({
        pageNo: isClientFilter ? 1 : page + 1,
        limit: isClientFilter ? 5000 : pageSize,
      });

      // Handle Manual Filters (Gender)
      if (selectedGender !== "") {
        queryParams.append('filter[gender]', selectedGender);
      }
      
      // Handle Approval Status
      queryParams.append('filter[status]', approvalStatus);

      // Handle DataGrid Filters
      filterModel.items.forEach((filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        ) {
          queryParams.append(`filter[${filter.columnField}]`, filter.value);
        }
      });

      // Handle Sorting
      if (sortModel.length > 0) {
        let sortField = sortModel[0].field;
        // Map frontend fields to backend fields if necessary
        if (sortField === 'name') sortField = 'first_name';
        
        queryParams.append("sort_by", sortField);
        queryParams.append("order", sortModel[0].sort.toUpperCase());
      }

      const response = await adminUserAction.getApprovalLists(queryParams);

      let filteredResults = response.results;

      // Client-side filtering fallback
      if (selectedGender) {
          filteredResults = filteredResults.filter(item => item.gender == selectedGender);
      }

      const formattedData = filteredResults.map((item) => ({
        id: item.user_id,
        name: `${item.first_name} ${item.last_name || ""}`,
        age: new Date().getFullYear() - new Date(item.birthday).getFullYear(),
        phone: item.phone || "N/A",
        email: item.email || "N/A",
        country: item.country || "N/A",
        gender: item.gender === 1 ? "Men" : "Women",
        created_at: new Date(item.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        updated_at: item.updated_at,
        birthday: item.birthday,
        avatar: {
          hash: item.hash,
          type: item.type,
          extension: item.extension,
        },
        has_pending_updates: item.has_pending_updates === 1,
      }));

      setCustomers(formattedData);
      setRowCount(isClientFilter ? formattedData.length : response.total);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterModel, sortModel, selectedGender, approvalStatus]);

  // Special effect to handle country selection change
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
    }
    const handleCountryChange = async () => {
      // 1. Update Filter Model locally for Country
      setFilterModel((prevModel) => {
         const newItems = prevModel.items.filter(
          (item) => item.columnField !== "country"
        );
        if (selectedCountry) {
          newItems.push({
            columnField: "country",
            value: selectedCountry.name,
            operatorValue: "contains",
          });
        }
        return { ...prevModel, items: newItems };
      });

      // 2. Adjust Pagination
      setPage(0);
      if (selectedCountry) {
        setPageSize(100);
      } else {
        setPageSize(25);
      }
    };
    
    handleCountryChange();
  }, [selectedCountry]);
  
  // Independent fetch trigger
  useEffect(() => {
     fetchData();
  }, [fetchData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBulkStatusChange = async (status, actionName) => {
    if (status === 30) {
        // Open Modal for Reject
        if (selectionModel.length === 0) return;
        setShowRejectModal(true);
        return;
    }

    if (window.confirm(`Are you sure you want to ${actionName} ${selectionModel.length} users?`)) {
        try {
            setLoading(true);
            await Promise.all(selectionModel.map(id => 
                adminUserAction.updateUserStatus({ id, approval: status })
            ));
            setSelectionModel([]);
            await fetchData();
        } catch (err) {
            console.error(`Error ${actionName} users:`, err);
        } finally {
            setLoading(false);
        }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectionModel.length} users?`)) {
        try {
            setLoading(true);
            await Promise.all(selectionModel.map(id => 
                adminUserAction.deleteUser({ id })
            ));
            setSelectionModel([]);
            await fetchData();
        } catch (err) {
            console.error("Error deleting users:", err);
        } finally {
            setLoading(false);
        }
    }
  };

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSelectedGender("");
    setApprovalStatus("10");
    setSortModel([{ field: 'updated_at', sort: 'desc' }]);
    setPage(0);
  };

  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setApprovalStatus(newStatus);
      setPage(0);
    }
  };

  const desktopColumns = [
    {
      field: "name",
      headerName: "Customer",
      flex: 1.5,
      filterOperators: [containsOperator],
      renderCell: (params) => (
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar 
                src={getProfileImageUrl(params.row.avatar)} 
                sx={{ width: 35, height: 35 }}
            />
            <Box display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight="bold">
                    {params.value}
                </Typography>
                {params.row.has_pending_updates && (
                    <Chip 
                        label="Pending Updates" 
                        size="small" 
                        color="warning" 
                        sx={{ height: 20, fontSize: '0.65rem', mt: 0.5, width: 'fit-content' }} 
                    />
                )}
            </Box>
          </Box>
      )
    },
    {
      field: "gender",
      headerName: "Gender",
      flex: 0.7,
      filterable: false,
    },
    {
      field: "country",
      headerName: "Location",
      flex: 1,
      filterable: true,
      filterOperators: [containsOperator],
      renderCell: (params) => (
          params.value !== "N/A" ? <Typography variant="body2">{params.value}</Typography> : "N/A"
      )
    },
    {
        field: "updated_at",
        headerName: "Updated At",
        flex: 1,
        filterable: false,
        valueFormatter: (params) => {
            if (!params.value) return "N/A";
            return new Date(params.value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.7,
      filterable: false,
      renderCell: (params) => (
        <Button
            component={Link}
            to={`/admin/user/approval/${params.row.id}`}
            variant="contained"
            size="small"
            sx={{
                backgroundColor: colors.blueAccent[500],
                '&:hover': {
                    backgroundColor: colors.blueAccent[700],
                }
            }}
        >
          Review
        </Button>
      ),
    },
  ];

  const mobileColumns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { 
        field: "action", 
        headerName: "Action", 
        flex: 0.7,
        renderCell: (params) => (
            <Link
              to={`/admin/user/detail/${params.row.id}`}
              className="text-primary text-decoration-none"
            >
              Review
            </Link>
          ),
    },
  ];

  // Custom Checkbox with Tooltip for disabled rows
  const CustomCheckbox = React.forwardRef((props, ref) => {
    const { disabled, ...other } = props;
    
    if (disabled) {
      return (
        <Tooltip title="Please review the profile" placement="top">
          <span style={{ display: 'inline-block' }}>
             <Checkbox 
                ref={ref} 
                disabled 
                {...other} 
                sx={{ 
                    opacity: 0.3, 
                    cursor: 'not-allowed !important',
                    color: colors.grey[500]
                }} 
            />
          </span>
        </Tooltip>
      );
    }
    return <Checkbox ref={ref} {...other} />;
  });

  return (
    <div className="container mt-4">
        {/* Header Section - Simplified for ProfitPulse look */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h3" fontWeight="bold">
                Approvals
            </Typography>
            
            <Box display="flex" gap={2}>
                 <Box sx={{ bgcolor: colors.primary[400], p: 0.5, borderRadius: '12px' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        textColor="secondary" 
                        indicatorColor="secondary"
                        sx={{ minHeight: 'auto', '& .MuiTab-root': { minHeight: 40, borderRadius: '8px' } }}
                    >
                        <Tab label="Users" />
                        <Tab label="Reports" />
                    </Tabs>
                 </Box>
                 <Box display="flex" alignItems="center" gap={1} bgcolor={colors.primary[400]} p={1} borderRadius="12px">
                     <Avatar sx={{ width: 30, height: 30, bgcolor: colors.blueAccent[500] }}>A</Avatar>
                     <Typography variant="body2" fontWeight="bold">Admin</Typography>
                 </Box>
            </Box>
        </Box>

      {tabValue === 0 && (
        <Card elevation={0} sx={{ backgroundColor: 'transparent', backgroundImage: 'none' }}>
            
            {/* ProfitPulse Filter Bar */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: 2, 
                    mb: 3, 
                    backgroundColor: colors.primary[400], 
                    borderRadius: '16px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                     {/* Status Toggle Slider */}
                     <ToggleButtonGroup
                        value={approvalStatus}
                        exclusive
                        onChange={handleStatusChange}
                        size="small"
                        sx={{ 
                            backgroundColor: theme.palette.background.default,
                            borderRadius: '12px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '12px !important',
                                px: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                color: colors.grey[300],
                                '&.Mui-selected': {
                                    backgroundColor: approvalStatus === "10" ? colors.blueAccent[600] : colors.redAccent[600],
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: approvalStatus === "10" ? colors.blueAccent[700] : colors.redAccent[700],
                                    }
                                }
                            }
                        }}
                     >
                        <ToggleButton value="10">Pending</ToggleButton>
                        <ToggleButton value="30">Rejected</ToggleButton>
                     </ToggleButtonGroup>

                     <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                     {/* Gender Filter */}
                     <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Gender</InputLabel>
                        <Select
                            value={selectedGender}
                            label="Gender"
                            onChange={(e) => {
                                setSelectedGender(e.target.value);
                                setPage(0);
                            }}
                            sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                        >
                            <MenuItem value=""><em>Any Gender</em></MenuItem>
                            <MenuItem value="1">Men</MenuItem>
                            <MenuItem value="2">Women</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Location Filter */}
                    <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.name}
                        value={selectedCountry}
                        onChange={(event, newValue) => {
                            setSelectedCountry(newValue);
                            setPage(0);
                        }}
                        sx={{ width: 220 }}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Location" 
                                variant="outlined" 
                                size="small"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: theme.palette.background.default }
                                }}
                            />
                        )}
                    />
                    
                    {(selectedCountry || selectedGender) && (
                         <Button onClick={handleClearFilters} color="error" size="small">
                            Clear
                         </Button>
                    )}
                </Box>

                <Box display="flex" gap={2} alignItems="center">
                    {/* Bulk Actions */}
                    {selectionModel.length > 0 && (
                        <Box display="flex" gap={1}>
                             <Button 
                                variant="contained" 
                                sx={{ backgroundColor: "#10B981", color: "#fff", borderRadius: '12px', '&:hover': { backgroundColor: "#059669" } }} // Success Green
                                onClick={() => handleBulkStatusChange(20, 'approve')}
                                size="small"
                            >
                                Approve ({selectionModel.length})
                            </Button>
                             {approvalStatus !== "30" && (
                                 <Button 
                                    variant="contained" 
                                    sx={{ backgroundColor: "#F59E0B", color: "#fff", borderRadius: '12px', '&:hover': { backgroundColor: "#D97706" } }} // Warning Orange
                                    onClick={() => handleBulkStatusChange(30, 'reject')}
                                    size="small"
                                >
                                    Reject ({selectionModel.length})
                                </Button>
                             )}
                            <Button 
                                variant="contained" 
                                color="error" 
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                sx={{ borderRadius: '12px' }}
                            >
                                Delete ({selectionModel.length})
                            </Button>
                        </Box>
                     )}

                    {/* Sort By */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                            value={sortModel[0]?.field || "updated_at"}
                            label="Sort by"
                            onChange={(e) => {
                                setSortModel([{ field: e.target.value, sort: sortModel[0]?.sort || 'desc' }]);
                                setPage(0);
                            }}
                            startAdornment={<SortIcon sx={{ mr: 1, fontSize: 18, color: colors.grey[400] }} />}
                            sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                        >
                            <MenuItem value="name">Customer</MenuItem>
                            <MenuItem value="updated_at">Updated At</MenuItem>
                            <MenuItem value="gender">Gender</MenuItem>
                            <MenuItem value="country">Location</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                            value={sortModel[0]?.sort || "desc"}
                            label="Order"
                            onChange={(e) => {
                                setSortModel([{ field: sortModel[0]?.field || 'updated_at', sort: e.target.value }]);
                                setPage(0);
                            }}
                            sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                        >
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

          
          <Box 
            sx={{ 
                height: 700, 
                width: '100%',
                '& .MuiDataGrid-root': {
                    border: 'none',
                    backgroundColor: colors.primary[400],
                    borderRadius: '16px',
                    padding: '10px'
                },
                '& .MuiDataGrid-cell': {
                    borderBottom: 'none',
                    paddingY: '10px'
                },
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'transparent',
                    borderBottom: `1px solid ${colors.grey[800]}`,
                    color: colors.grey[300],
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                },
                '& .MuiDataGrid-row': {
                    borderRadius: '8px',
                    marginTop: '5px',
                    '&:hover': {
                         backgroundColor: colors.blueAccent[800]
                    }
                },
                '& .MuiDataGrid-footerContainer': {
                    borderTop: 'none',
                    justifyContent: 'center'
                },
                '& .MuiCheckbox-root': {
                    color: `${colors.grey[500]} !important`,
                },
                '& .row-pending-updates .MuiDataGrid-checkboxInput': {
                    opacity: 0.3,
                    cursor: 'not-allowed',
                },
            }}
          >
            <DataGrid
              page={page}
              loading={loading}
              pageSize={pageSize}
              rows={customers}
              rowCount={rowCount}
              pagination
              paginationMode={(selectedCountry || selectedGender) ? "client" : "server"}
              sortingMode={(selectedCountry || selectedGender) ? "client" : "server"}
              rowsPerPageOptions={[25, 50, 100]}
              checkboxSelection
              selectionModel={selectionModel}
              onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
              onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
              components={{ Toolbar: GridToolbar, Checkbox: CustomCheckbox }}
              columns={isMobile ? mobileColumns : desktopColumns}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              filterModel={filterModel}
              onFilterModelChange={(newModel) => {
                const updatedModel = {
                  ...newModel,
                  items: newModel.items.map((item) => ({
                    ...item,
                    operatorValue: "contains", // force contains
                  })),
                };
                setFilterModel(updatedModel);
                setPage(0);
              }}
              getRowId={(row) => row.id}
              isRowSelectable={(params) => !params.row.has_pending_updates}
              getRowClassName={(params) => params.row.has_pending_updates ? 'row-pending-updates' : ''}
              componentsProps={{
                pagination: {
                   sx: {
                    color: theme.palette.text.primary,
                    "& .MuiTablePagination-selectLabel": { color: theme.palette.text.primary },
                    "& .MuiTablePagination-input": { color: theme.palette.text.primary },
                    "& .MuiTablePagination-displayedRows": { color: theme.palette.text.primary },
                    "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
                    "& .MuiButtonBase-root": { color: theme.palette.text.primary },
                    "& .MuiPaginationItem-root": { color: theme.palette.text.primary },
                   },
                },
              }}
            />
          </Box>
        </Card>
      )}

      {tabValue === 1 && <ReportList />}
      
      {/* Reject Reason Modal */}
      <RejectNewUserModal
          show={showRejectModal}
          onClose={handleCloseRejectModal}
          selectedReason={selectedReason}
          onSelectChange={handleSelectChange}
          customReason={customReason}
          onCustomReasonChange={handleCustomReasonChange}
          onSave={handleSaveReject}
          reasons={reasons}
      />
    </div>
  );
};

export default Contacts;
